import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient, downloadFile } from "./client";

export type PaymentMethod = "EFECTIVO" | "TRANSFERENCIA" | "CHEQUE" | "TARJETA" | "NEQUI" | "DAVIPLATA" | "OTRO" | "CREDITO" | "MIXTO";

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  EFECTIVO: "Efectivo",
  TRANSFERENCIA: "Transferencia",
  CHEQUE: "Cheque",
  TARJETA: "Tarjeta",
  NEQUI: "Nequi",
  DAVIPLATA: "Daviplata",
  OTRO: "Otro",
  CREDITO: "Crédito (fiado)",
  MIXTO: "Pago mixto",
};

/** Métodos que se pueden usar como una línea de pago mixto — ni CREDITO ni MIXTO (ver SALE_PAYMENT_METHODS en el backend). */
export const SALE_PAYMENT_METHOD_OPTIONS: PaymentMethod[] = ["EFECTIVO", "TRANSFERENCIA", "CHEQUE", "TARJETA", "NEQUI", "DAVIPLATA", "OTRO"];

export interface CashShiftRecord {
  id: string;
  branchId: string;
  branch?: { id: string; name: string } | null;
  openedAt: string;
  openingCash: string;
  closedAt: string | null;
  closingCash: string | null;
  expectedCash: string | null;
  difference: string | null;
  status: "ABIERTO" | "CERRADO";
  openedBy?: { id: string; fullName: string } | null;
  closedBy?: { id: string; fullName: string } | null;
}

/** Turno abierto de UNA sucursal — puede haber uno por sucursal a la vez, ya no uno por organización. */
export function useOpenShift(branchId?: string) {
  return useQuery({
    queryKey: ["store", "pos", "open-shift", branchId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<CashShiftRecord | null>>("/pos/shifts/open", { params: { branchId } });
      return data.data;
    },
    enabled: !!branchId,
    staleTime: 10_000,
  });
}

export function useCashShifts(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ["store", "pos", "shifts", page, pageSize],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<CashShiftRecord[]>>("/pos/shifts", { params: { page, pageSize } });
      return data;
    },
  });
}

export function useOpenCashShift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ branchId, openingCash }: { branchId: string; openingCash: number | string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<CashShiftRecord>>("/pos/shifts", { branchId, openingCash });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "pos"] }),
  });
}

export function useCloseCashShift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, closingCash }: { id: string; closingCash: number | string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<CashShiftRecord>>(`/pos/shifts/${id}/close`, { closingCash });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "pos"] }),
  });
}

export interface CashShiftPaymentBreakdownRow {
  paymentMethod: string;
  salesTotal: number;
  refundsTotal: number;
}

export interface CashShiftBreakdown {
  shiftId: string;
  status: "ABIERTO" | "CERRADO";
  openingCash: number;
  cashInTotal: number;
  cashOutTotal: number;
  byPaymentMethod: CashShiftPaymentBreakdownRow[];
  grandSalesTotal: number;
  grandRefundsTotal: number;
  expectedCash: number | null;
  closingCash: number | null;
  difference: number | null;
}

/** Desglose de un turno de caja por método de pago (no solo efectivo) — funciona con el turno ABIERTO o ya CERRADO. Ver pos.service.ts::getCashShiftBreakdown. */
export function useCashShiftBreakdown(shiftId: string | undefined | null) {
  return useQuery({
    queryKey: ["store", "pos", "shift-breakdown", shiftId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<CashShiftBreakdown>>(`/pos/shifts/${shiftId}/breakdown`);
      return data.data;
    },
    enabled: !!shiftId,
  });
}

export type CashMovementType = "ENTRADA" | "SALIDA";

export const CASH_MOVEMENT_TYPE_LABEL: Record<CashMovementType, string> = {
  ENTRADA: "Entrada de efectivo",
  SALIDA: "Salida de efectivo",
};

export interface CashMovementRecord {
  id: string;
  cashShiftId: string;
  type: CashMovementType;
  amount: string;
  concept: string;
  notes: string | null;
  createdAt: string;
  createdBy?: { id: string; fullName: string } | null;
}

/** Movimientos de efectivo (entradas/salidas manuales) de un turno — no son ventas ni devoluciones (ej. retiro para pagar un domicilio, cambio adicional). Se descuentan/suman al efectivo esperado al cerrar el turno. */
export function useCashMovements(cashShiftId: string | undefined) {
  return useQuery({
    queryKey: ["store", "pos", "shift-movements", cashShiftId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<CashMovementRecord[]>>(`/pos/shifts/${cashShiftId}/movements`);
      return data.data;
    },
    enabled: !!cashShiftId,
  });
}

export function useRegisterCashMovement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ cashShiftId, type, amount, concept, notes }: { cashShiftId: string; type: CashMovementType; amount: number | string; concept: string; notes?: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<CashMovementRecord>>(`/pos/shifts/${cashShiftId}/movements`, { type, amount, concept, notes });
      return data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["store", "pos", "shift-movements", variables.cashShiftId] });
    },
  });
}

export interface StoreCustomerRecord {
  id: string;
  name: string;
  documentId: string | null;
  phone: string | null;
  email: string | null;
  /** Puntos de fidelización acumulados — 1 punto por cada $1.000 del total de cada venta con este cliente. */
  pointsBalance: number;
}

export function useStoreCustomers(search?: string) {
  return useQuery({
    queryKey: ["store", "pos", "customers", search],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<StoreCustomerRecord[]>>("/pos/customers", { params: { search } });
      return data.data;
    },
  });
}

export function useCreateStoreCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; documentId?: string; phone?: string; email?: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<StoreCustomerRecord>>("/pos/customers", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "pos", "customers"] }),
  });
}

export interface SaleLineRecord {
  id: string;
  productId: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  discountAmount: string;
  subtotal: string;
  warrantyEndDate: string | null;
  product?: { id: string; sku: string; name: string; unit: string };
}

export interface SalePaymentRecord {
  id: string;
  paymentMethod: PaymentMethod;
  amount: string;
  reference: string | null;
}

export interface SaleRecord {
  id: string;
  branchId: string;
  branch?: { id: string; name: string } | null;
  saleNumber: number;
  saleDate: string;
  subtotal: string;
  taxTotal: string;
  discountTotal: string;
  total: string;
  paymentMethod: PaymentMethod;
  amountPaid: string;
  changeGiven: string;
  status: "COMPLETADA" | "ANULADA";
  pointsEarned: number;
  storeCustomer?: { id: string; name: string } | null;
  createdBy?: { id: string; fullName: string } | null;
  lines: SaleLineRecord[];
  /** Detalle de pago mixto (2+ filas) o de método único (1 fila) — vacío solo si fue una venta 100% a crédito sin abono. */
  payments?: SalePaymentRecord[];
}

export interface ListSalesFilters {
  page?: number;
  pageSize?: number;
  cashShiftId?: string;
  status?: string;
  saleNumber?: number;
}

export function useSales(filters: ListSalesFilters = {}) {
  return useQuery({
    queryKey: ["store", "pos", "sales", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<SaleRecord[]>>("/pos/sales", { params: filters });
      return data;
    },
  });
}

export interface SalePaymentLine {
  paymentMethod: PaymentMethod;
  amount: number | string;
  reference?: string;
}

export interface CreateSalePayload {
  branchId: string;
  storeCustomerId?: string;
  paymentMethod?: PaymentMethod;
  amountPaid: number | string;
  /** Pago mixto: 2+ métodos en la misma venta. Si se manda, gana sobre paymentMethod/amountPaid — ver pos.service.ts::createSale. */
  payments?: SalePaymentLine[];
  discountTotal?: number | string;
  lines: Array<{ productId: string; quantity: number | string }>;
  /** Solo si paymentMethod === "CREDITO" (ver api/store-credits.ts). */
  creditInstallments?: number;
  creditFirstDueDate?: string;
  creditIntervalDays?: number;
  /** El cajero ya confirmó la advertencia de "vas a vender por debajo del costo" — ver pos.service.ts::createSale. Sin esto, el backend rechaza la venta con BELOW_COST_CONFIRMATION_REQUIRED si alguna línea queda con precio efectivo menor al costo. */
  acknowledgeBelowCost?: boolean;
}

export function useCreateSale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSalePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<SaleRecord>>("/pos/sales", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "pos"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export function useVoidSale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<SaleRecord>>(`/pos/sales/${id}/void`, {});
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "pos"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

/** Descarga el recibo/factura interna de una venta en PDF (NO es factura electrónica DIAN — ver pos-receipt.service.ts). */
export function downloadSaleReceipt(saleId: string, saleNumber: number) {
  return downloadFile(`/pos/sales/${saleId}/receipt.pdf`, `venta-${saleNumber}.pdf`);
}

// ---------- Devoluciones ----------
// A diferencia de anular una venta (la anula ENTERA), esto devuelve solo
// algunos ítems — ver pos.service.ts::createSaleReturn.

export interface ReturnableLine {
  saleLineId: string;
  productId: string;
  product?: { id: string; sku: string; name: string; unit: string };
  quantitySold: number;
  unitPrice: number;
  alreadyReturned: number;
  returnable: number;
}

export function useReturnableLines(saleId: string | null) {
  return useQuery({
    queryKey: ["store", "pos", "returnable", saleId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<{ sale: SaleRecord; lines: ReturnableLine[] }>>(`/pos/sales/${saleId}/returnable`);
      return data.data;
    },
    enabled: !!saleId,
  });
}

export interface SaleReturnLineRecord {
  id: string;
  saleLineId: string;
  productId: string;
  product?: { id: string; sku: string; name: string; unit: string };
  quantity: string;
  unitPrice: string;
  refundAmount: string;
}

export interface SaleReturnRecord {
  id: string;
  saleId: string;
  branchId: string;
  branch?: { id: string; name: string } | null;
  totalRefund: string;
  refundMethod: PaymentMethod;
  cashShiftId: string | null;
  notes: string | null;
  createdBy?: { id: string; fullName: string } | null;
  createdAt: string;
  lines: SaleReturnLineRecord[];
}

export interface CreateSaleReturnPayload {
  lines: Array<{ saleLineId: string; quantity: number | string }>;
  notes?: string;
  /** Cómo se le devuelve el dinero al cliente — por defecto EFECTIVO. Si es EFECTIVO y hay un turno de caja abierto, se descuenta del efectivo esperado al cerrarlo. */
  refundMethod?: PaymentMethod;
}

export function useCreateSaleReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ saleId, payload }: { saleId: string; payload: CreateSaleReturnPayload }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<SaleReturnRecord>>(`/pos/sales/${saleId}/returns`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "pos"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export interface ListSaleReturnsFilters {
  page?: number;
  pageSize?: number;
  saleId?: string;
}

export function useSaleReturns(filters: ListSaleReturnsFilters = {}) {
  return useQuery({
    queryKey: ["store", "pos", "returns", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<SaleReturnRecord[]>>("/pos/returns", { params: filters });
      return data;
    },
  });
}

export interface WarrantyRow {
  saleLineId: string;
  saleId: string;
  saleNumber: number;
  saleDate: string;
  productId: string;
  productName: string;
  productSku: string;
  quantity: string;
  customerId: string | null;
  customerName: string | null;
  customerDocumentId: string | null;
  warrantyEndDate: string;
  isExpired: boolean;
}

export interface ListWarrantiesFilters {
  search?: string;
  status?: "ACTIVA" | "VENCIDA";
}

export function useWarranties(filters: ListWarrantiesFilters = {}) {
  return useQuery({
    queryKey: ["store", "pos", "warranties", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<WarrantyRow[]>>("/pos/warranties", { params: filters });
      return data.data;
    },
  });
}
